export type CountryId = "uganda" | "mozambique" | "ethiopia" | "somalia" | "tanzania";

export type Country = {
  id: CountryId;
  name: string;
  role: "Pilot country" | "Regional pipeline";
  lat: number;
  lon: number;
  languages: string[];
  readinessBriefs: string[];
  populationM: number;
  povertyPct: number;
  literacyPct: number;
  internetPct: number;
  communities: number;
  validators: number;
  serviceJourneys: number;
  fieldSites: number;
  phraseTarget: number;
  qaPassPct: number;
  adoptionPct: number;
  baselineNeed: string;
  countryAllocation: number;
};

export const countries: Country[] = [
  {
    id: "uganda",
    name: "Uganda",
    role: "Pilot country",
    lat: 1.3733,
    lon: 32.2903,
    languages: ["Luganda", "Runyankore/Rukiga", "Lusoga", "Acholi"],
    readinessBriefs: ["Ateso", "Lugbara", "Runyoro/Rutooro", "Langi/Lango"],
    populationM: 50.0,
    povertyPct: 59.8,
    literacyPct: 77.0,
    internetPct: 27.0,
    communities: 112,
    validators: 168,
    serviceJourneys: 14,
    fieldSites: 12,
    phraseTarget: 38000,
    qaPassPct: 94.6,
    adoptionPct: 66,
    baselineNeed: "No approved source library, validated answer set or country-specific comprehension evidence at start.",
    countryAllocation: 74000,
  },
  {
    id: "mozambique",
    name: "Mozambique",
    role: "Pilot country",
    lat: -18.6657,
    lon: 35.5296,
    languages: ["Emakhuwa", "Makhuwa-Meetto/Emeetto", "Mwani/Kimwani", "Makonde/Shimakonde"],
    readinessBriefs: ["Changana/Xitsonga", "Ciyao/Yao", "Tewe/Chiute", "Nyungwe/Cinyungwe"],
    populationM: 34.6,
    povertyPct: 81.4,
    literacyPct: 61.7,
    internetPct: 20.5,
    communities: 96,
    validators: 144,
    serviceJourneys: 14,
    fieldSites: 12,
    phraseTarget: 36000,
    qaPassPct: 93.8,
    adoptionPct: 58,
    baselineNeed: "No approved packs, country source library or language-specific cost profile at start.",
    countryAllocation: 74000,
  },
  {
    id: "ethiopia",
    name: "Ethiopia",
    role: "Regional pipeline",
    lat: 9.145,
    lon: 40.4897,
    languages: ["Amharic", "Oromo", "Somali", "Tigrinya"],
    readinessBriefs: ["Afar", "Sidamo"],
    populationM: 129.7,
    povertyPct: 68.7,
    literacyPct: 51.8,
    internetPct: 19.4,
    communities: 74,
    validators: 96,
    serviceJourneys: 10,
    fieldSites: 8,
    phraseTarget: 28000,
    qaPassPct: 90.4,
    adoptionPct: 42,
    baselineNeed: "Regional source readiness and language validation route require partner channel confirmation.",
    countryAllocation: 92000,
  },
  {
    id: "somalia",
    name: "Somalia",
    role: "Regional pipeline",
    lat: 5.1521,
    lon: 46.1996,
    languages: ["Somali", "Maay", "Benadiri"],
    readinessBriefs: ["Arabic service terms", "Diaspora referral terms"],
    populationM: 18.7,
    povertyPct: 69.0,
    literacyPct: 41.0,
    internetPct: 27.6,
    communities: 46,
    validators: 72,
    serviceJourneys: 8,
    fieldSites: 6,
    phraseTarget: 18000,
    qaPassPct: 88.2,
    adoptionPct: 38,
    baselineNeed: "Security, data protection and partner-routing assumptions require dedicated validation.",
    countryAllocation: 78000,
  },
  {
    id: "tanzania",
    name: "Tanzania",
    role: "Regional pipeline",
    lat: -6.369,
    lon: 34.8888,
    languages: ["Kiswahili", "Sukuma", "Chaga", "Haya"],
    readinessBriefs: ["Gogo", "Nyamwezi"],
    populationM: 67.4,
    povertyPct: 49.2,
    literacyPct: 82.0,
    internetPct: 31.6,
    communities: 68,
    validators: 84,
    serviceJourneys: 10,
    fieldSites: 8,
    phraseTarget: 24000,
    qaPassPct: 91.6,
    adoptionPct: 46,
    baselineNeed: "Regional route can build from Kiswahili baseline but still requires local-language service validation.",
    countryAllocation: 76000,
  },
];

export const programTotals = {
  durationMonths: 18,
  stageOneBudget: 200000,
  pilotLanguages: 16,
  pilotCountries: 2,
  targetUsers: 2000,
  coreSharedBudget: 52000,
};

export const dashboardSeries = [
  { month: "M1", collected: 3000, validated: 1400, qa: 82 },
  { month: "M3", collected: 17500, validated: 9200, qa: 87 },
  { month: "M6", collected: 42000, validated: 28600, qa: 90 },
  { month: "M9", collected: 68000, validated: 51000, qa: 92 },
  { month: "M12", collected: 102000, validated: 74200, qa: 94 },
  { month: "M18", collected: 132000, validated: 109000, qa: 95 },
];

export type TopicNode = {
  id: string;
  label: string;
  x: number;
  y: number;
  size: number;
  kind: "core" | "evidence" | "delivery" | "risk" | "finance";
  detail: string;
};

export const topicNodes: TopicNode[] = [
  { id: "access", label: "Inclusive language access", x: 50, y: 50, size: 15, kind: "core", detail: "The program outcome tying language assets, public-service journeys and governed AI release into a single access model." },
  { id: "languages", label: "Language portfolio", x: 17, y: 26, size: 8, kind: "delivery", detail: "Full validation packs and readiness briefs across Mozambique, Uganda and regional expansion tracks." },
  { id: "sources", label: "Approved source base", x: 35, y: 18, size: 7, kind: "evidence", detail: "Source mapping, service-journey metadata and monthly update controls." },
  { id: "service", label: "Service journeys", x: 67, y: 24, size: 8, kind: "delivery", detail: "Education, youth opportunity, civil documentation, local administration and referral navigation." },
  { id: "safeguards", label: "Safeguards and ethics", x: 84, y: 35, size: 8, kind: "risk", detail: "Data minimization, consent, refusal accuracy, safeguarding and no personalized legal or medical advice." },
  { id: "mel", label: "MEL and learning", x: 22, y: 72, size: 8, kind: "evidence", detail: "Mixed-methods implementation study with task testing, interviews, focus groups and cost evidence." },
  { id: "cost", label: "Cost model and sustainability", x: 41, y: 82, size: 8, kind: "finance", detail: "Cost per successful navigation, shared core costs, country allocations and scale economics." },
  { id: "partners", label: "Partners and ecosystem", x: 68, y: 78, size: 8, kind: "delivery", detail: "Country directors, program managers, field experts, reviewers, validators and adoption partners." },
  { id: "policy", label: "Policy adoption", x: 86, y: 66, size: 8, kind: "finance", detail: "Evidence-to-adoption pathway for partner channels, public-service owners and Stage 2 scale." },
  { id: "qa", label: "QA and release gates", x: 52, y: 28, size: 7, kind: "risk", detail: "Grounding score, refusal tests, reviewer adjudication and field issue triage before releases." },
];

export const topicEdges = [
  ["access", "languages"],
  ["access", "service"],
  ["access", "mel"],
  ["access", "cost"],
  ["access", "partners"],
  ["access", "policy"],
  ["languages", "sources"],
  ["languages", "qa"],
  ["sources", "service"],
  ["sources", "qa"],
  ["service", "safeguards"],
  ["service", "policy"],
  ["safeguards", "qa"],
  ["qa", "mel"],
  ["mel", "cost"],
  ["cost", "policy"],
  ["partners", "policy"],
  ["partners", "service"],
  ["partners", "languages"],
];

export const resources = [
  {
    category: "Mozambique and Uganda FID package",
    title: "Mozambique-Uganda FID Program Dossier",
    type: "PDF",
    href: "/resources/Namuli_Access_Mozambique_Uganda_FID_Program_Dossier.pdf",
    detail: "Full program design, country setup, MEL, governance, budget and implementation architecture.",
  },
  {
    category: "Mozambique and Uganda FID package",
    title: "Complete Program Logical Framework",
    type: "PDF",
    href: "/resources/Namuli_Access_Uganda_Mozambique_Complete_Program_One_Pager.pdf",
    detail: "Landscape one-page logical framework for the combined Uganda-Mozambique program.",
  },
  {
    category: "Mozambique and Uganda FID package",
    title: "Logic Model and Outcome Map",
    type: "PDF",
    href: "/resources/Namuli_Access_Mozambique_Uganda_Logic_Model_and_Outcome_Map.pdf",
    detail: "Enhanced logic model architecture, outcome mapping, strategy map and evaluation questions for the Mozambique-Uganda program.",
  },
  {
    category: "Mozambique and Uganda FID package",
    title: "Mozambique Program One-Pager",
    type: "PDF",
    href: "/resources/Namuli_Access_Mozambique_Program_One_Pager.pdf",
    detail: "Country-specific logic, metrics, languages, safeguards and implementation evidence.",
  },
  {
    category: "Mozambique and Uganda FID package",
    title: "Uganda Program One-Pager",
    type: "PDF",
    href: "/resources/Namuli_Access_Uganda_Program_One_Pager.pdf",
    detail: "Country-specific logic, metrics, languages, safeguards and implementation evidence.",
  },
  {
    category: "Mozambique and Uganda FID package",
    title: "Logical Framework One-Pagers",
    type: "PDF",
    href: "/resources/Namuli_Access_Uganda_Mozambique_Logical_Framework_One_Pagers.pdf",
    detail: "Combined, Mozambique-only and Uganda-only landscape logical framework pages in one file.",
  },
  {
    category: "Mozambique and Uganda FID package",
    title: "Program Design Workbook",
    type: "XLSX",
    href: "/resources/Namuli_Access_Mozambique_Uganda_Program_Design_Workbook.xlsx",
    detail: "Detailed budget, program design, work breakdown and operating workbook.",
  },
  {
    category: "Mozambique and Uganda FID package",
    title: "Administrative Workbook",
    type: "XLSX",
    href: "/resources/Namuli_Access_Mozambique_Uganda_Administrative_Workbook.xlsx",
    detail: "Administrative planning workbook for staffing, governance, procurement and operating controls.",
  },
  {
    category: "Mozambique and Uganda FID package",
    title: "Complete Submission Bundle",
    type: "ZIP",
    href: "/resources/Namuli_Access_Mozambique_Uganda_FID_Package_20260605.zip",
    detail: "Compressed package containing the Mozambique-Uganda dossier, workbooks, one-pagers and supporting files.",
  },
  {
    category: "Ethiopia, Somalia and Tanzania package",
    title: "Ethiopia-Somalia-Tanzania Program Dossier",
    type: "PDF",
    href: "/resources/Namuli_Access_Ethiopia_Somalia_Tanzania_Program_Dossier.pdf",
    detail: "Parallel regional program dossier for use in non-FID channels and future submission pathways.",
  },
  {
    category: "Ethiopia, Somalia and Tanzania package",
    title: "Logic Model and Outcome Map",
    type: "PDF",
    href: "/resources/Namuli_Access_Ethiopia_Somalia_Tanzania_Logic_Model_and_Outcome_Map.pdf",
    detail: "Enhanced regional logic model architecture, outcome mapping, strategy map and evaluation questions for Ethiopia, Somalia and Tanzania.",
  },
  {
    category: "Ethiopia, Somalia and Tanzania package",
    title: "Program Design Workbook",
    type: "XLSX",
    href: "/resources/Namuli_Access_Ethiopia_Somalia_Tanzania_Program_Design_Workbook.xlsx",
    detail: "Regional program design, budget and implementation workbook for Ethiopia, Somalia and Tanzania.",
  },
  {
    category: "Ethiopia, Somalia and Tanzania package",
    title: "Administrative Workbook",
    type: "XLSX",
    href: "/resources/Namuli_Access_Ethiopia_Somalia_Tanzania_Administrative_Workbook.xlsx",
    detail: "Administrative planning workbook for country operations, staffing and controls.",
  },
  {
    category: "Ethiopia, Somalia and Tanzania package",
    title: "Complete Program Bundle",
    type: "ZIP",
    href: "/resources/Namuli_Access_Ethiopia_Somalia_Tanzania_Package_20260605.zip",
    detail: "Compressed package containing the Ethiopia-Somalia-Tanzania dossier, workbooks and supporting files.",
  },
  {
    category: "Namuli Labs institutional files",
    title: "Brand and Marketing System",
    type: "PDF",
    href: "/resources/Namuli_Labs_Brand_and_Marketing_System.pdf",
    detail: "Namuli Labs identity, palette, language and partnership presentation system.",
  },
  {
    category: "Namuli Labs institutional files",
    title: "Business Strategy and Operating Model",
    type: "PDF",
    href: "/resources/Namuli_Labs_Business_Strategy_and_Operating_Model.pdf",
    detail: "Institutional strategy, operating model, market positioning, partnerships and sustainability logic.",
  },
  {
    category: "Namuli Labs institutional files",
    title: "Corporate and Organizational Manual",
    type: "PDF",
    href: "/resources/Namuli_Labs_Corporate_and_Organizational_Manual.pdf",
    detail: "Governance, delegations of authority, staffing, compliance and administration.",
  },
  {
    category: "Namuli Labs institutional files",
    title: "Draft Statutes and Bylaws",
    type: "PDF",
    href: "/resources/Namuli_Labs_Draft_Statutes_and_Bylaws.pdf",
    detail: "Institutional formation, board structure and country operations clauses.",
  },
  {
    category: "Namuli Labs institutional files",
    title: "Internal Formation and Readiness File",
    type: "PDF",
    href: "/resources/Namuli_Labs_Internal_Formation_and_Readiness_File.pdf",
    detail: "Internal institutional setup file covering registration route, readiness controls and formation decisions.",
  },
];
